import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  ClipboardCheck, Loader2, FileText, FilePenLine, Send, Euro, Calendar,
  ArrowRight,
} from "lucide-react";
import api from "@/lib/api";

/**
 * Compact card shown on Dashboard for Consultores (and admins) — surfaces
 * the RTs-pending panel counts + the top 3 oldest pending leads. Clicking
 * anywhere in the card navigates to ``/rts-pendentes`` for the full view.
 */
const REASON_META = {
  proposta_por_gerar:  { label: "por gerar",  icon: FileText, tone: "bg-amber-500/15 text-amber-500 border-amber-500/30" },
  proposta_por_enviar: { label: "por enviar", icon: FilePenLine, tone: "bg-sky-500/15 text-sky-500 border-sky-500/30" },
  aguarda_decisao:     { label: "aguarda",    icon: Send, tone: "bg-violet-500/15 text-violet-500 border-violet-500/30" },
  deposito_pendente:   { label: "depósito",   icon: Euro, tone: "bg-rose-500/15 text-rose-500 border-rose-500/30" },
  contrato_pendente:   { label: "contrato",   icon: ClipboardCheck, tone: "bg-emerald-500/15 text-emerald-500 border-emerald-500/30" },
};

export default function ConsultorRTsCard() {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);

  useEffect(() => {
    let alive = true;
    api.get("/rts-pendentes").then((r) => {
      if (alive) setData(r.data);
    }).catch(() => {
      if (alive) setData(null);
    }).finally(() => {
      if (alive) setLoading(false);
    });
    return () => { alive = false; };
  }, []);

  const total = (data?.counts?.completed_pending ?? 0);
  const scheduled = (data?.counts?.scheduled ?? 0);

  return (
    <div className="bg-card border border-border rounded-2xl p-5 lg:p-6 mb-6" data-testid="consultor-rts-card">
      <div className="flex items-start justify-between gap-4 mb-4">
        <div className="flex items-start gap-3">
          <div className="w-9 h-9 rounded-full bg-primary/15 border border-primary/25 flex items-center justify-center flex-shrink-0">
            <ClipboardCheck className="w-4 h-4 text-primary" />
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground font-semibold">Consultor de Cuidados</div>
            <h3 className="font-ui text-lg text-foreground mt-0.5">RTs pendentes</h3>
            <div className="text-[11.5px] text-muted-foreground mt-1">
              {loading ? "A carregar…" : (
                <>
                  <b data-testid="rts-card-total-pending">{total}</b> realizada(s) por fechar
                  {scheduled > 0 && <> · <b>{scheduled}</b> agendada(s)</>}
                </>
              )}
            </div>
          </div>
        </div>
        <Link
          to="/rts-pendentes"
          data-testid="rts-card-link"
          className="inline-flex items-center gap-1.5 text-xs bg-muted hover:bg-muted/70 border border-border text-foreground rounded-full px-3 py-1.5 font-semibold shrink-0"
        >
          Abrir painel <ArrowRight className="w-3.5 h-3.5" />
        </Link>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-6 text-muted-foreground text-sm">
          <Loader2 className="w-4 h-4 animate-spin mr-2" /> A carregar…
        </div>
      ) : total === 0 && scheduled === 0 ? (
        <div className="text-sm text-muted-foreground py-4 text-center">
          Sem RTs pendentes. Ótimo trabalho! 🎉
        </div>
      ) : (
        <>
          {/* Compact reason strip */}
          <div className="flex flex-wrap gap-2 mb-4">
            {scheduled > 0 && (
              <span className="inline-flex items-center gap-1.5 text-[10.5px] uppercase tracking-[0.12em] px-2.5 py-1 rounded-full border bg-primary/10 text-primary border-primary/25">
                <Calendar className="w-3 h-3" />
                agendadas · {scheduled}
              </span>
            )}
            {Object.entries(data?.counts?.by_reason || {}).map(([r, cnt]) => {
              if (!cnt) return null;
              const meta = REASON_META[r] || REASON_META.proposta_por_gerar;
              const Icon = meta.icon;
              return (
                <span key={r} className={`inline-flex items-center gap-1.5 text-[10.5px] uppercase tracking-[0.12em] px-2.5 py-1 rounded-full border ${meta.tone}`}>
                  <Icon className="w-3 h-3" /> {meta.label} · {cnt}
                </span>
              );
            })}
          </div>

          {/* Top 3 oldest pending */}
          {(data?.completed_pending || []).slice(0, 3).map((lead) => (
            <Link
              key={lead.id}
              to={`/leads/${lead.id}`}
              data-testid={`rts-card-preview-${lead.id}`}
              className="flex items-center justify-between gap-3 px-3 py-2 rounded-lg hover:bg-muted/40 transition-colors"
            >
              <div className="min-w-0">
                <div className="text-[13px] font-medium text-foreground truncate">{lead.name}</div>
                <div className="text-[11px] text-muted-foreground">
                  {lead.pending_reason_label} · há {lead.pending_days ?? "?"} dia(s)
                </div>
              </div>
              <ArrowRight className="w-3.5 h-3.5 text-muted-foreground flex-shrink-0" />
            </Link>
          ))}
        </>
      )}
    </div>
  );
}
